import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getJob } from "../api/jobs";
import { notifyJobError } from "../errors";
import type { Job, JobStatus } from "../types";

const TERMINAL: ReadonlySet<JobStatus> = new Set<JobStatus>(["success", "failed"]);
const notifiedFailedJobs = new Set<string>();

function isTerminal(s?: JobStatus): boolean {
  return !!s && TERMINAL.has(s);
}

export function useJobPolling(jobId: string | null | undefined) {
  const query = useQuery<Job>({
    queryKey: ["job", jobId],
    queryFn: () => getJob(jobId!),
    enabled: !!jobId,
    refetchInterval: (q) => (isTerminal(q.state.data?.status) ? false : 2000),
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  useEffect(() => {
    if (!query.data || query.data.status !== "failed") return;
    if (notifiedFailedJobs.has(query.data.id)) return;
    notifiedFailedJobs.add(query.data.id);
    notifyJobError(query.data);
  }, [query.data]);

  return {
    data: query.data,
    isPending: query.isPending,
    isTerminal: isTerminal(query.data?.status),
    isFailed: query.data?.status === "failed",
  };
}
